import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { AutoComplete, List, Tooltip } from 'antd'
import styled from 'styled-components'
import _ from 'lodash'
import { colours } from '@qflow/theme'

import { ColourButton, icons } from 'portal/lib/primitives'

import { AlertRecipients } from './AlertRecipients'
import {
  AdditionalText,
  AlertRuleContainer,
  Description,
  SettingArea,
  TextArea
} from './sharedStyledComponents'
import { EnableAlertRuleSwitch } from './EnableSwitch'

export const SupplierWatchlist = ({
  projectId,
  alertKey,
  alertRules,
  setAlertRules,
  cannotEnable
}) => {
  const [autoCompleteValue, setAutoCompleteValue] = useState('')
  const [options, setOptions] = useState<{ value: string; label: string }[]>([])

  const allCompanies = useSelector(
    (state: any) => state.companies.data.companies
  )

  const companyOptions: { value: string; label: string }[] = useMemo(() => {
    if (!allCompanies || !Array.isArray(allCompanies)) {
      return []
    }
    return allCompanies.map(company => ({
      value: company.name,
      label: company.name
    }))
  }, [allCompanies])

  const addValueToAlertRules = (value: string) => {
    const updatedAlertRules = _.cloneDeep(alertRules)
    let hasAddedValues = false

    const parts = value
      .split(',')
      .map(part => part.trim())
      .filter(Boolean)

    // Add each part if it's not already in the list
    parts.forEach(part => {
      if (!updatedAlertRules[alertKey].watchedSuppliers.includes(part)) {
        updatedAlertRules[alertKey].watchedSuppliers.push(part)
        hasAddedValues = true
      }
    })

    if (hasAddedValues) {
      // Sort watchedSuppliers alphabetically
      updatedAlertRules[alertKey].watchedSuppliers.sort((a, b) =>
        a.localeCompare(b)
      )

      setAlertRules(updatedAlertRules)
    }

    // Clear the input field after adding
    setAutoCompleteValue('')
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      const value = e.target.value.trim()
      addValueToAlertRules(value)
    }
  }

  const handleSearch = value => {
    if (value.length >= 2) {
      // Simulate fetching data based on the input
      const filteredOptions = companyOptions.filter(option =>
        option.value.toLowerCase().includes(value.toLowerCase())
      )
      setOptions(filteredOptions)
    } else {
      setOptions([]) // Clear options if input is less than 2 characters
    }
  }

  const handleSelectClick = value => {
    addValueToAlertRules(value)
  }

  if (!alertRules[alertKey]) {
    return null
  }

  return (
    <>
      <AlertRuleContainer>
        <TextArea>
          <Description>Supplier Watchlist</Description>
          <AdditionalText>
            Notifies you when a delivery is made by a supplier on your
            watchlist.
          </AdditionalText>
        </TextArea>
        <SettingArea>
          <EnableAlertRuleSwitch
            alertKey={alertKey}
            alertRules={alertRules}
            setAlertRules={setAlertRules}
            cannotEnable={cannotEnable}
            ariaLabel="Enable Supplier Watchlist"
          />
          <SupplierWatchlistWrapper>
            <AutoComplete
              id="supplier-autocomplete"
              allowClear
              options={options}
              onSearch={handleSearch}
              style={{ width: '404px' }}
              onChange={value => setAutoCompleteValue(value)}
              onInputKeyDown={handleKeyDown}
              onSelect={handleSelectClick}
              value={autoCompleteValue}
              placeholder="Enter or search supplier name"
            />
            <AdditionalText style={{ paddingLeft: '0.125rem' }}>
              <div>
                Add supplier to watchlist by typing their name and pressing
                Enter.
              </div>
              <div>
                Add multiple suppliers at once by separating names with commas.
              </div>
            </AdditionalText>
            <List
              size="small"
              bordered
              dataSource={alertRules[alertKey].watchedSuppliers}
              locale={{
                emptyText: (
                  <div>
                    {!alertRules[alertKey].isEnabled ? (
                      'No suppliers in watchlist.'
                    ) : (
                      <span style={{ color: colours.RED_DARKMODE }}>
                        One or more suppliers required
                      </span>
                    )}
                  </div>
                )
              }}
              style={{
                marginTop: '0.5rem',
                backgroundColor: colours.WHITE,
                maxHeight: '13rem',
                overflowY: 'auto'
              }}
              renderItem={(supplier: string) => (
                <List.Item style={{ padding: '0' }}>
                  <SupplierWrapper>
                    {supplier}
                    <Tooltip title="Remove supplier">
                      <ColourButton.BasicBorderless
                        name={`remove-supplier-${supplier.replace(/\s+/g, '-').toLowerCase()}`}
                        aria-label={'Remove supplier'}
                        style={{
                          padding: '0.25rem 9px',
                          borderRadius: '50%',
                          color: colours.RED_DARKMODE
                        }}
                        onClick={() => {
                          const updatedAlertRules = _.cloneDeep(alertRules)
                          updatedAlertRules[alertKey].watchedSuppliers =
                            updatedAlertRules[alertKey].watchedSuppliers.filter(
                              s => s !== supplier
                            )
                          setAlertRules(updatedAlertRules)
                        }}
                      >
                        <icons.TrashCan colour="currentColor" />
                      </ColourButton.BasicBorderless>
                    </Tooltip>
                  </SupplierWrapper>
                </List.Item>
              )}
            />
          </SupplierWatchlistWrapper>
        </SettingArea>
      </AlertRuleContainer>
      <AlertRecipients
        projectId={projectId}
        alertKey={alertKey}
        alertRules={alertRules}
        setAlertRules={setAlertRules}
      />
    </>
  )
}

const SupplierWatchlistWrapper = styled.div`
  margin-top: 0.25rem;
  min-width: 442px;
`
const SupplierWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  padding: 0.25rem 0.25rem 0.25rem 0.625rem;
`
